import React, { useState } from 'react';

import styled from 'styled-components';

import { Button } from '../../../components/Button';
import { Icon } from '../../../components/Common/Icon';
import { ICON_ID } from '../../../components/Common/Icon/const';
import { useGetApplicationVersions } from '../../../services/application/queries';
import { formatDate } from '../../../utils/format';

const HEADER_TITLE = ['게시일시', '프로그램명', '버전', '다운로드'] as const;
const ListTable = () => {
	const { data, isSuccess } = useGetApplicationVersions();
	const [downloadingUrl, setDownloadingUrl] = useState<string | null>(null);

	const handleDownload = async (url: string) => {
		setDownloadingUrl(url);
		try {
			const response = await fetch(url);
			const blob = await response.blob();
			const downloadUrl = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = downloadUrl;

			const urlObj = new URL(url);
			const pathname = urlObj.pathname;
			const filename = pathname.substring(pathname.lastIndexOf('/') + 1);

			link.download = decodeURIComponent(filename);

			document.body.appendChild(link);
			link.click();

			document.body.removeChild(link);
			window.URL.revokeObjectURL(downloadUrl);
		} catch (error) {
			console.error('파일 다운로드 중 오류 발생:', error);
		} finally {
			setDownloadingUrl(null);
		}
	};

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
			<div style={{ fontSize: '1.6rem', fontWeight: 'bold' }}>다운로더 버전</div>
			<Wrapper style={{ height: 'fit-content' }}>
				<div className="header-row">
					{HEADER_TITLE.map((title) => (
						<div key={'title--' + title} className="header-item">
							{title}
						</div>
					))}
				</div>
				{isSuccess &&
					data.map((el, idx) => (
						<div className="data-row" key={'data--row' + idx}>
							<div className="item">{formatDate(new Date(el.updatedAt), 'yyyy-MM-dd hh:mm')}</div>
							<div className="item">{el.typeName}</div>
							<div className="item">{el.version}</div>
							<div className="item">
								<Button
									$buttonType="LINE"
									$height="auto"
									$padding="0.4rem"
									$color="PRIMARY"
									onClick={() => {
										handleDownload(el.url);
									}}
									disabled={downloadingUrl === el.url}
									className="download-button"
								>
									{downloadingUrl === el.url ? (
										<LoadingSpinner />
									) : (
										<Icon iconID={ICON_ID.FILE} width={'2.4rem'} height={'2.4rem'} $defaultColor="primary_600" />
									)}
								</Button>
							</div>
						</div>
					))}
			</Wrapper>
		</div>
	);
};

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	border-radius: 8px;
	overflow: hidden;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

	.header-row {
		display: flex;
		align-items: center;
		padding: 1.2rem 1rem;
		background-color: ${({ theme }) => theme.colors.gray[100]};
		border-bottom: 1px solid ${({ theme }) => theme.colors.gray[200]};
		font-weight: 600;
		font-size: 14px;

		.header-item {
			padding: 0.4rem;
			flex: 1;
			display: flex;
			justify-content: center;
			text-align: center;

			&:nth-child(1) {
				min-width: 18rem;
			}
			&:nth-child(2) {
				min-width: 18rem;
			}
			&:nth-child(4) {
				min-width: 7rem;
			}
		}
	}

	.data-row {
		display: flex;
		align-items: center;
		padding: 1.2rem 1rem;
		border-bottom: 1px solid ${({ theme }) => theme.colors.gray[200]};
		transition: background-color 0.2s ease;

		&:hover {
			background-color: ${({ theme }) => theme.colors.gray[100]};
		}

		.item {
			padding: 0.4rem;
			flex: 1;
			display: flex;
			justify-content: center;
			text-align: center;
			font-size: 14px;

			&:nth-child(1) {
				min-width: 18rem;
			}
			&:nth-child(2) {
				min-width: 18rem;
			}
			&:nth-child(4) {
				min-width: 7rem;
			}
		}
	}

	.download-button {
		&:hover {
			background-color: ${({ theme }) => theme.colors.gray[100]};
			border-radius: 4px;
		}
	}

	@media (max-width: 1024px) {
		.header-row .header-item,
		.data-row .item {
			${({ theme }) => theme.font.body[3]}

			&:nth-child(1) {
				min-width: 14rem;
			}
			&:nth-child(2) {
				min-width: 14rem;
			}
		}
	}

	@media (max-width: 768px) {
		.header-row .header-item,
		.data-row .item {
			padding: 0.3rem;

			&:nth-child(1) {
				min-width: 12rem;
			}
			&:nth-child(2) {
				min-width: 12rem;
			}
			&:nth-child(3) {
				min-width: 5rem;
			}
			&:nth-child(4) {
				min-width: 5rem;
			}
		}
	}
`;

const LoadingSpinner = styled.div`
	width: 2.4rem;
	height: 2.4rem;
	border: 2px solid ${({ theme }) => theme.colors.gray[200]};
	border-top: 2px solid ${({ theme }) => theme.colors.primary[600]};
	border-radius: 50%;
	animation: spin 1s linear infinite;

	@keyframes spin {
		0% {
			transform: rotate(0deg);
		}
		100% {
			transform: rotate(360deg);
		}
	}
`;

export default ListTable;
